'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ArrowDownGlyph } from '../../../internal/icons.js';
import { dayKey, dayLabel, sameDay } from '../../../internal/day-groups.js';
import { LoadMore } from '../../../components/data-grid/load-more/index.js';
import { Skeleton } from '../../../components/feedback/skeleton/index.js';
// useLayoutEffect warns during server rendering; the scroll work only matters in the browser.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const DEFAULT_MESSAGES = {
    label: 'Messages',
    newMessages: 'New messages',
    today: 'Today',
    yesterday: 'Yesterday',
    jumpToLatest: 'Jump to latest',
    unseen: (n) => (n === 1 ? '1 new message' : `${n} new messages`),
    loadOlder: 'Load older messages',
    loadingOlder: 'Loading older messages…',
    olderError: 'Couldn’t load older messages.',
    loading: 'Loading messages',
};
const FIVE_MINUTES = 5 * 60_000;
function time(input) {
    return (input instanceof Date ? input : new Date(input)).getTime();
}
/**
 * Whether `next` continues `previous`'s group: same author, same day and
 * within `windowMs`. Exported for products that render rows in their own
 * virtualised list.
 */
export function shouldGroupMessages(previous, next, options = {}) {
    const { windowMs = FIVE_MINUTES, timeZone } = options;
    if (!previous || previous.authorId == null || next.authorId == null)
        return false;
    if (previous.authorId !== next.authorId)
        return false;
    if (!sameDay(previous.time, next.time, timeZone))
        return false;
    const gap = time(next.time) - time(previous.time);
    return gap >= 0 && gap < windowMs;
}
/** "Today", "Yesterday" or a date: the text of a day separator. */
export function formatMessageDay(date, options = {}) {
    const { labels, ...rest } = options;
    return dayLabel(date, rest, labels);
}
/** The "Today" line between days. Exported for virtualised lists. */
export function MessageTimelineDaySeparator({ children, label, className, ...rest }) {
    return (_jsx("div", { role: "separator", "aria-label": label ?? (typeof children === 'string' ? children : undefined), ...rest, className: cx('mn-message-timeline__separator', className), "data-kind": "day", children: _jsx("span", { className: "mn-message-timeline__separator-text", children: children }) }));
}
/** The "New messages" line before the first unread message. Exported for virtualised lists. */
export function MessageTimelineUnreadDivider({ children, label, className, ...rest }) {
    return (_jsx("div", { role: "separator", "aria-label": label ?? (typeof children === 'string' ? children : undefined), ...rest, className: cx('mn-message-timeline__separator', className), "data-kind": "unread", children: _jsx("span", { className: "mn-message-timeline__separator-text", children: children }) }));
}
function prefersReducedMotion() {
    return (typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}
function MessageTimelineInner({ items, renderItem, firstUnreadId, hasOlder = false, loadingOlder = false, olderError = false, onLoadOlder, onRetryOlder, loadOlderThreshold = 240, stickToBottom = true, atBottomThreshold = 64, onAtBottomChange, jumpToLatest = true, header, footer, status, empty, loading = false, error, highlightedId, scrollToId, groupWindowMs = FIVE_MINUTES, now, locale, timeZone, handleRef, messages: messagesProp, scrollerClassName, className, ...rest }, ref) {
    const m = { ...DEFAULT_MESSAGES, ...messagesProp };
    const scroller = useRef(null);
    const content = useRef(null);
    const [atBottom, setAtBottom] = useState(true);
    const [unseen, setUnseen] = useState(0);
    const [settling, setSettling] = useState(false);
    const atBottomRef = useRef(true);
    const snapshot = useRef({
        height: 0,
        top: 0,
    });
    const requested = useRef(false);
    const initialised = useRef(false);
    const onAtBottomRef = useRef(onAtBottomChange);
    onAtBottomRef.current = onAtBottomChange;
    const behavior = (wanted = 'smooth') => prefersReducedMotion() ? 'auto' : wanted;
    const scrollToBottom = useCallback((how = 'auto') => {
        const el = scroller.current;
        if (!el)
            return;
        if (typeof el.scrollTo === 'function')
            el.scrollTo({ top: el.scrollHeight, behavior: how });
        else
            el.scrollTop = el.scrollHeight;
    }, []);
    const scrollToItem = useCallback((id, how = 'auto') => {
        const nodes = scroller.current?.querySelectorAll('[data-message-id]') ?? [];
        const el = Array.from(nodes).find((node) => node.dataset.messageId === id);
        if (!el)
            return false;
        el.scrollIntoView({ block: 'center', behavior: how });
        return true;
    }, []);
    useImperativeHandle(handleRef, () => ({
        scrollToBottom: (how) => scrollToBottom(behavior(how ?? 'smooth')),
        scrollToItem: (id, how) => scrollToItem(id, behavior(how ?? 'smooth')),
    }), [scrollToBottom, scrollToItem]);
    const updateAtBottom = useCallback(() => {
        const el = scroller.current;
        if (!el)
            return;
        const bottom = el.scrollHeight - el.scrollTop - el.clientHeight <= atBottomThreshold;
        if (bottom !== atBottomRef.current) {
            atBottomRef.current = bottom;
            setAtBottom(bottom);
            onAtBottomRef.current?.(bottom);
        }
        if (bottom)
            setUnseen(0);
    }, [atBottomThreshold]);
    const maybeLoadOlder = useCallback(() => {
        const el = scroller.current;
        if (!el || !onLoadOlder || !hasOlder || loadingOlder || olderError || requested.current)
            return;
        const nearTop = el.scrollTop <= loadOlderThreshold;
        // A hidden scroller (no height) never counts as unfilled, or it would load the whole history.
        const unfilled = el.clientHeight > 0 && el.scrollHeight <= el.clientHeight;
        if (nearTop || unfilled) {
            requested.current = true;
            onLoadOlder();
        }
    }, [onLoadOlder, hasOlder, loadingOlder, olderError, loadOlderThreshold]);
    // A new load can be requested once the previous one has settled.
    useEffect(() => {
        if (!loadingOlder)
            requested.current = false;
    }, [loadingOlder, items.length, olderError]);
    // Keep the reader's place: prepends keep the view still, appends follow the
    // bottom only when the reader is there. Runs before paint.
    useIsoLayoutEffect(() => {
        const el = scroller.current;
        if (!el)
            return;
        const prev = snapshot.current;
        const first = items[0]?.id;
        const last = items[items.length - 1]?.id;
        if (!initialised.current) {
            if (items.length > 0) {
                initialised.current = true;
                if (!(scrollToId && scrollToItem(scrollToId)))
                    scrollToBottom();
            }
        }
        else if (prev.first !== undefined && first !== prev.first) {
            const k = items.findIndex((item) => item.id === prev.first);
            if (k > 0) {
                el.scrollTop = prev.top + (el.scrollHeight - prev.height);
                setSettling(true);
            }
        }
        if (initialised.current && last !== prev.last && prev.last !== undefined) {
            if (stickToBottom && atBottomRef.current)
                scrollToBottom();
            else {
                const from = items.findIndex((item) => item.id === prev.last);
                const added = from >= 0 ? items.length - 1 - from : 0;
                if (added > 0)
                    setUnseen((n) => n + added);
            }
        }
        snapshot.current = { first, last, height: el.scrollHeight, top: el.scrollTop };
    }, [items]);
    // aria-busy for a moment after a prepend, so screen readers do not read old history as new.
    useEffect(() => {
        if (!settling)
            return;
        const t = setTimeout(() => setSettling(false), 300);
        return () => clearTimeout(t);
    }, [settling]);
    // Fill the view: keep loading older pages while the history is shorter than the scroller.
    useEffect(() => {
        maybeLoadOlder();
    }, [maybeLoadOlder, items.length]);
    // Deep links.
    useEffect(() => {
        if (scrollToId && initialised.current)
            scrollToItem(scrollToId, behavior('smooth'));
    }, [scrollToId, scrollToItem]);
    // Content that grows after render (images, link previews) keeps the bottom pinned.
    useEffect(() => {
        const node = content.current;
        if (!node || typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(() => {
            const el = scroller.current;
            if (!el)
                return;
            if (stickToBottom && atBottomRef.current)
                scrollToBottom();
            snapshot.current = { ...snapshot.current, height: el.scrollHeight, top: el.scrollTop };
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [stickToBottom, scrollToBottom]);
    const onScroll = () => {
        const el = scroller.current;
        if (el)
            snapshot.current = { ...snapshot.current, height: el.scrollHeight, top: el.scrollTop };
        updateAtBottom();
        maybeLoadOlder();
    };
    const jump = () => {
        scrollToBottom(behavior('smooth'));
        setUnseen(0);
        // The button disappears; keep focus in the conversation.
        scroller.current?.focus({ preventScroll: true });
    };
    const showList = !loading && !error && items.length > 0;
    const rows = [];
    if (showList) {
        let previous;
        items.forEach((item, index) => {
            const newDay = !previous || dayKey(previous.time, timeZone) !== dayKey(item.time, timeZone);
            const unread = firstUnreadId != null && item.id === firstUnreadId;
            const grouped = !newDay &&
                !unread &&
                shouldGroupMessages(previous, item, {
                    windowMs: groupWindowMs,
                    ...(timeZone ? { timeZone } : {}),
                });
            const label = newDay
                ? dayLabel(item.time, { now, locale, timeZone }, { today: m.today, yesterday: m.yesterday })
                : '';
            rows.push(_jsxs(Fragment, { children: [newDay ? _jsx(MessageTimelineDaySeparator, { children: label }) : null, unread ? (_jsx(MessageTimelineUnreadDivider, { children: m.newMessages })) : null, _jsx("div", { className: "mn-message-timeline__item", "data-message-id": item.id, "data-highlighted": highlightedId != null && highlightedId === item.id ? '' : undefined, children: renderItem(item, { grouped, index, previous }) })] }, item.id));
            previous = item;
        });
    }
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-message-timeline', className), children: [_jsx("div", { ref: scroller, role: "log", "aria-live": "polite", "aria-relevant": "additions", "aria-label": m.label, "aria-busy": loading || loadingOlder || settling || undefined, 
                // A focusable scroll region (WCAG 2.1.1): keyboard users scroll the log with the arrow keys.
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex: 0, className: cx('mn-message-timeline__scroller', scrollerClassName), onScroll: onScroll, children: _jsxs("div", { ref: content, className: "mn-message-timeline__content", children: [loading ? (_jsx("div", { className: "mn-message-timeline__skeleton", "aria-label": m.loading, role: "img", children: [0.6, 0.85, 0.45, 0.7].map((w, i) => (_jsxs("div", { className: "mn-message-timeline__skeleton-row", children: [_jsx(Skeleton, { shape: "circle", width: "2rem", height: "2rem" }), _jsxs("div", { className: "mn-message-timeline__skeleton-lines", children: [_jsx(Skeleton, { shape: "text", width: "8rem" }), _jsx(Skeleton, { shape: "text", width: `${w * 100}%` })] })] }, i))) })) : error ? (_jsx("div", { className: "mn-message-timeline__error", children: error })) : items.length === 0 ? (_jsx("div", { className: "mn-message-timeline__empty", children: empty ?? header })) : (_jsxs(_Fragment, { children: [hasOlder && onLoadOlder ? (_jsx(LoadMore, { className: "mn-message-timeline__older", hasMore: true, loading: loadingOlder, error: olderError, onLoadMore: onLoadOlder, ...(onRetryOlder ? { onRetry: onRetryOlder } : {}), label: m.loadOlder, loadingLabel: m.loadingOlder, errorLabel: m.olderError, endLabel: null, showCount: false })) : header ? (_jsx("div", { className: "mn-message-timeline__header", children: header })) : null, rows] })), footer && !loading && !error ? (_jsx("div", { className: "mn-message-timeline__footer", children: footer })) : null] }) }), jumpToLatest && showList && !atBottom ? (_jsxs("button", { type: "button", className: "mn-message-timeline__jump", onClick: jump, children: [_jsx(ArrowDownGlyph, {}), unseen > 0 ? m.unseen(unseen) : m.jumpToLatest] })) : null, _jsx("div", { role: "status", "aria-live": "polite", className: "mn-message-timeline__status", children: status })] }));
}
/**
 * A scrolling conversation: MessageRows separated by day, a "New messages"
 * divider, older history loaded on scroll-up with the view held still, and
 * new messages followed while the reader is at the bottom. A polite
 * `role="log"` announces messages as they arrive.
 */
export const MessageTimeline = forwardRef(MessageTimelineInner);
//# sourceMappingURL=message-timeline.js.map